import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useTitle } from "../../hooks/useTitle";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { colors, useMediaQuery, useTheme } from "@mui/material";
import { isProductionMode } from "../../utils/helper";
import CornerRibbon from "../../components/CornerRibbon";
import workz360 from "../../images/workz360.png";
import { CardBase } from "../../components/global";

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: "14px 28px",
  fontSize: "1rem",
  fontWeight: 600,
  textTransform: "none",
  backgroundColor: "#12903C",
  color: "#ffffff",
  boxShadow: "0 4px 14px rgba(18, 144, 60, 0.4)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-100%",
    width: "100%",
    height: "100%",
    background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)",
    transition: "left 0.5s ease",
  },
  "&:hover": {
    backgroundColor: "#0f7a32",
    transform: "translateY(-2px)",
    boxShadow: "0 8px 24px rgba(18, 144, 60, 0.6)",
    "&::before": {
      left: "100%",
    },
  },
  "&:active": {
    transform: "translateY(0)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "10px 20px",
    fontSize: "0.875rem",
  },
}));

const SignupPage = () => {
  const navigate = useNavigate();
  useTitle("Signup");
  const isProduction = React.useMemo(() => isProductionMode(), []);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        minHeight: "100vh",
        height: { xs: "100%", sm: "100vh" },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 4 },
        backgroundImage: "url('/background-home.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: { xs: "scroll", sm: "fixed" },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: { xs: "100%", sm: 1000 },
          position: "relative",
        }}
      >
        <Box
          sx={{
            mb: { xs: 3, sm: 5 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: 1.5, sm: 2 },
          }}
        >
          <Box
            component="img"
            src={workz360}
            alt="Workz360"
            sx={{
              height: { xs: 44, sm: 64 },
              width: "auto",
              objectFit: "contain",
              filter: "drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15))",
              transition: "transform 0.3s ease",
              "&:hover": {
                transform: "scale(1.05)",
              },
            }}
          />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              letterSpacing: 0.5,
              textAlign: "center",
              fontSize: { xs: "1.5rem", sm: "2.125rem" },
            }}
          >
            Get started on Workz360!
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              textAlign: "center",
              maxWidth: { xs: "100%", sm: 600 },
              fontSize: { xs: "0.875rem", sm: "1rem" },
              px: { xs: 1, sm: 0 },
            }}
          >
            Choose your account type to begin your journey
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 3 },
            justifyContent: "center",
            alignItems: "stretch",
          }}
        >
          <Box sx={{ flex: { xs: "none", sm: 1 }, width: { xs: "100%", sm: "auto" }, maxWidth: { xs: "100%", sm: 420 }, position: "relative", display: "flex" }}>
            <CardBase
              useGlassCard={true}
              nested={false}
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "translateY(-8px) scale(1.02)",
                },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  overflow: "hidden",
                  height: { xs: 120, sm: 280 },
                  width: "100%",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.1) 100%)",
                    zIndex: 1,
                  },
                }}
              >
                <Box
                  component="img"
                  src={"https://source.unsplash.com/744oGeqpxPQ?w=300"}
                  alt="Workers"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                    transition: "transform 0.4s ease",
                    "&:hover": {
                      transform: "scale(1.1)",
                    },
                  }}
                />
              </Box>
              <Box sx={{ p: { xs: 1.25, sm: 3 }, flexGrow: 1, display: "flex", flexDirection: "column" }}>
                <Typography
                  gutterBottom
                  variant="h5"
                  component="div"
                  sx={{
                    fontWeight: 700,
                    mb: { xs: 0.5, sm: 1.5 },
                    color: "#12903C",
                    fontSize: { xs: "0.85rem", sm: "1.5rem" },
                    lineHeight: { xs: 1.2, sm: 1.5 },
                  }}
                >
                  I'm looking for Workers
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    mb: { xs: 0.75, sm: 2 },
                    lineHeight: { xs: 1.3, sm: 1.6 },
                    flexGrow: 1,
                    fontSize: { xs: "0.65rem", sm: "1rem" },
                  }}
                >
                  Find skilled workers that match your business needs. Post jobs and connect with talented professionals.
                </Typography>
              </Box>
              <Box sx={{ p: { xs: 1.25, sm: 3 }, pt: 0 }}>
                <StyledButton
                  fullWidth
                  variant="contained"
                  onClick={() => navigate("/signup/company")}
                >
                  Signup as Company
                </StyledButton>
              </Box>
            </CardBase>
          </Box>
          <Box sx={{ flex: { xs: "none", sm: 1 }, width: { xs: "100%", sm: "auto" }, maxWidth: { xs: "100%", sm: 420 }, position: "relative", display: "flex" }}>
            <CardBase
              useGlassCard={true}
              nested={false}
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "translateY(-8px) scale(1.02)",
                },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  overflow: "hidden",
                  height: { xs: 120, sm: 280 },
                  width: "100%",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.1) 100%)",
                    zIndex: 1,
                  },
                }}
              >
                <Box
                  component="img"
                  src={"https://source.unsplash.com/sMKUYIasyDM?w=300"}
                  alt="Work"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                    transition: "transform 0.4s ease",
                    "&:hover": {
                      transform: "scale(1.1)",
                    },
                  }}
                />
              </Box>
              <Box sx={{ p: { xs: 1.25, sm: 3 }, flexGrow: 1, display: "flex", flexDirection: "column" }}>
                <Typography
                  gutterBottom
                  variant="h5"
                  component="div"
                  sx={{
                    fontWeight: 700,
                    mb: { xs: 0.5, sm: 1.5 },
                    color: "#12903C",
                    fontSize: { xs: "0.85rem", sm: "1.5rem" },
                    lineHeight: { xs: 1.2, sm: 1.5 },
                  }}
                >
                  I'm looking for work
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    mb: { xs: 0.75, sm: 2 },
                    lineHeight: { xs: 1.3, sm: 1.6 },
                    flexGrow: 1,
                    fontSize: { xs: "0.65rem", sm: "1rem" },
                  }}
                >
                  Discover opportunities that fit your skills. Join our platform and find the perfect job for you.
                </Typography>
              </Box>
              <Box sx={{ p: { xs: 1.25, sm: 3 }, pt: 0 }}>
                <StyledButton
                  fullWidth
                  variant="contained"
                  href="https://play.google.com/store/search?q=workz360&c=apps"
                  style={{ textAlign: "center" }}
                >
                  Signup as Independent Professional
                </StyledButton>
              </Box>
            </CardBase>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
export default SignupPage;
